import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Select,
  TextField,
} from '@mui/material'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import CustomBackdrop from 'common/components/CustomBackdrop'
import i18n from 'lang/idiomas'
import React from 'react'
import CommentsStepper from './ComentsStepper'
import * as Constants from './Constants'

import { styled } from '@mui/material/styles'
import DocsController from '../libs/DocsController'

const isHtmlPayload = (data) =>
  typeof data === 'string' && /<\s*(!doctype\b|html\b|head\b|body\b)/i.test(data)

const acciones = {
  F: {
    title: 'Firmar documento',
    text: '',
    placeholder:
      'Si desea aportar alguna observación a la firma de este documento, indíquelas aquí',
    actionTrue: 'Confirmar',
    actionFalse: 'Cancelar',
    loadingKey: 'firmandoDocumento',
    loadingDefault: 'Firmando documento',
  },
  R: {
    title: 'Rechazar documento',
    text: '¿Estás seguro de que deseas rechazar este documento? Si es así, introduce observaciones',
    placeholder:
      'Si desea aportar alguna observación a la firma de este documento, indíquelas aquí',
    actionTrue: 'Confirmar',
    actionFalse: 'Cancelar',
    loadingKey: 'rechazandoDocumento',
    loadingDefault: 'Rechazando documento',
  },
  O: {
    title: 'Observaciones',
    text: 'Añadir observaciones',
    placeholder: 'Observaciones',
    actionTrue: 'Confirmar',
    actionFalse: 'Cancelar',
    loadingKey: 'guardandoObservaciones',
    loadingDefault: 'Guardando observaciones',
  },
}

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`

const DocumentDialogs = ({
  setErrorStatus,
  docData,
  modalOpen,
  setModalOpen,
  action,
  setTrigger,
}) => {
  const [observaciones, setObservaciones] = React.useState('')
  const [authMethod, setAuthMethod] = React.useState('')
  const [authToken, setAuthToken] = React.useState('')
  const [tokenInputVisible, setTokenInputVisible] = React.useState(false)
  const [selectedFile, setSelectedFile] = React.useState(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const handleCloseModal = () => {
    setObservaciones('')
    setTokenInputVisible(false)
    setAuthToken('')
    setModalOpen(false)
    setSelectedFile(null)
  }

  const getTokenAction = async () => {
    setTokenInputVisible(true)
    const response = await DocsController.sendToken(
      authMethod || docData[0].tipos_2fa?.[0]?.tipo || '',
    )

    setErrorStatus({
      error: true,
      code: response.status,
      msg: response.data,
    })
  }

  const handleSubmitForm = async (action) => {
    if (
      action === 'F' &&
      docData.length === 1 &&
      docData[0]?.permitir_adjuntar_documentos === 'S' &&
      selectedFile
    ) {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsBinaryString(selectedFile)
      })
      await sendData(data)
    } else {
      await sendData()
    }
  }

  const sendData = async (file_data) => {
    const jsonData = {}

    if (file_data !== undefined) {
      jsonData['file'] = selectedFile
      jsonData['file_content'] = btoa(file_data)
      jsonData['sube_fichero'] = selectedFile !== null ? 'S' : 'N'
      jsonData['nombre'] = selectedFile.name
    }
    jsonData['tipo_gestion'] = action
    jsonData['token'] = authToken
    jsonData['observaciones'] = observaciones

    jsonData['documentos'] = docData.map((item) => ({ token_acceso: item.token_acceso }))

    jsonData['firma_2fa'] = docData[0]?.firma_2fa ?? 'N'

    if (docData.length === 1) jsonData['token_acceso'] = docData[0].token_acceso

    const response = await DocsController.sign(jsonData)
    setTrigger(true)

    const status = response?.status ?? 0
    const hasError = status === 0 || status > 300 || isHtmlPayload(response?.data)

    setErrorStatus({
      error: true,
      code: hasError ? status || 500 : status,
      msg: hasError
        ? i18n.t('errfirma', {
            defaultValue: 'Lo sentimos, no se ha podido firmar el documento.',
          })
        : response.data,
    })
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    setSelectedFile(file)
  }

  const isDisabled = () => {
    switch (action) {
      case 'O':
        return observaciones?.trim().length <= 0
      case 'F':
        if (docData.some((doc) => doc.firma_2fa === 'S') && authToken?.trim().length <= 0) {
          return true
        }

        if (docData.length === 1 && docData[0]?.permitir_adjuntar_documentos === 'S') {
          return selectedFile === null
        }

        return false
      default:
        return false
    }
  }

  return (
    <>
      <Dialog
        open={modalOpen}
        onClose={() => {
          if (isSubmitting) return
          handleCloseModal()
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          {docData.length <= 1 ? acciones[action]?.title : 'Firmar documentos'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {acciones[action]?.text}
          </DialogContentText>
          {action === 'O' && (
            <div style={{ overflow: 'auto' }}>
              <CommentsStepper
                commentsChat={docData[0]?.linea_observaciones_chat_list}
                commentsObs={docData[0]?.linea_observaciones_documento_list}
              />
            </div>
          )}
          <TextField
            autoFocus
            InputLabelProps={{ shrink: true }}
            multiline
            margin="dense"
            required={action === 'O'}
            id="name"
            label="Observaciones"
            placeholder={
              docData.length <= 1
                ? acciones[action]?.placeholder
                : 'Si desea aportar alguna observación a la firma de estos documentos, indíquelas aquí'
            }
            type="text"
            fullWidth
            variant="standard"
            value={observaciones}
            onChange={(event) => {
              setObservaciones(event.target.value)
            }}
          />
          {docData.length === 1 &&
            docData[0]?.permitir_adjuntar_documentos === 'S' &&
            action === 'F' && (
              <>
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  href="#file-upload"
                  sx={{ marginTop: '1rem' }}
                >
                  Adjuntar archivo firmado
                  <VisuallyHiddenInput type="file" name="file" onChange={handleFileChange} />
                </Button>
                {selectedFile !== undefined && selectedFile !== null ? (
                  <p style={{ marginTop: '1rem', color: 'gray' }}>
                    Archivo seleccionado: {selectedFile.name}
                  </p>
                ) : (
                  <></>
                )}
              </>
            )}
          {docData.some((doc) => doc.firma_2fa === 'S') && action === 'F' && (
            <>
              <DialogContentText id="alert-dialog-description" sx={{ marginTop: '2rem' }}>
                Escoge un método de verificación:
              </DialogContentText>
              <FormControl sx={{ minWidth: 120 }}>
                <Select
                  defaultValue={docData[0].tipos_2fa?.[0]?.tipo ?? ''}
                  variant="standard"
                  onChange={(e) => {
                    setAuthMethod(e.target.value)
                  }}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                >
                  {docData[0].tipos_2fa.map((item, index) => {
                    return (
                      <MenuItem key={index} value={item.tipo}>
                        {item.nombre}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
              <Button
                sx={{ marginLeft: '2rem' }}
                onClick={() => {
                  getTokenAction()
                }}
                autoFocus
              >
                Enviar token
              </Button>
              {tokenInputVisible && (
                <TextField
                  autoFocus
                  required
                  margin="dense"
                  id="name"
                  label="Código de confirmación"
                  type="text"
                  fullWidth
                  variant="standard"
                  onChange={(event) => {
                    setAuthToken(event.target.value)
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleCloseModal()
            }}
            autoFocus
          >
            {acciones[action]?.actionFalse}
          </Button>
          <Button
            onClick={async () => {
              setIsSubmitting(true)
              try {
                await handleSubmitForm(action)
              } finally {
                setIsSubmitting(false)
                handleCloseModal()
              }
            }}
            disabled={isDisabled() || isSubmitting}
            sx={{ color: 'red' }}
          >
            {acciones[action]?.actionTrue}
          </Button>
        </DialogActions>
      </Dialog>
      <CustomBackdrop
        open={isSubmitting}
        text={i18n.t(acciones[action]?.loadingKey, {
          defaultValue: acciones[action]?.loadingDefault,
        })}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      />
    </>
  )
}

export default DocumentDialogs
